import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { Router } from '@angular/router';
import { OnboardingOrchestratorService } from '../../../../core/services/onboarding-orchestrator.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ButtonModule } from '@openng/optimus-ui/button';
import { APP_ROUTES } from '../../../../core/constants/app-routes.const';
import { WebsiteSuggestionService } from '../../../../core/services/website-suggestion.service';

@Component({
  selector: 'app-onboarding-wait-screen',
  imports: [ButtonModule, FormsModule, InputTextModule],
  templateUrl: './onboarding-wait-screen.component.html',
  styleUrl: './onboarding-wait-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingWaitScreenComponent {
  readonly orchestrator = inject(OnboardingOrchestratorService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly websiteSuggestion = inject(WebsiteSuggestionService);

  readonly isVisible = this.orchestrator.isWaitScreenVisible;
  readonly progress = this.orchestrator.progress;
  readonly org = computed(() => this.auth.currentUser()?.organisation);
  readonly status = this.orchestrator.onboardingStatus;
  readonly domain = this.orchestrator.domain;

  readonly crawlState = this.orchestrator.crawlState;
  readonly discoveryState = this.orchestrator.discoveryState;
  readonly generationState = this.orchestrator.generationState;
  readonly progressPercent = this.orchestrator.progressPercent;
  readonly retrying = this.orchestrator.retrying;
  readonly failureHeadline = this.orchestrator.failureHeadline;
  readonly suggestedWebsite = this.websiteSuggestion.suggestion;

  /** The address the user is correcting, seeded with the one that failed. */
  readonly website = signal('');

  constructor() {
    effect(() => {
      // This component is mounted for the life of the app, so nothing else takes the field down
      // when the screen closes — and `logout()` does not reload the page. Left standing, the
      // address one organisation failed on would seed the next one's retry and be crawled.
      if (!this.isVisible()) {
        this.website.set('');
        this.websiteSuggestion.clear();
        return;
      }

      if (this.status() !== 'FAILED') return;
      // The organisation's own value, not orchestrator.domain(), whose 'your website' fallback is
      // a label rather than an address and would be submitted as one.
      const domain = this.auth.currentUser()?.organisation?.domain ?? '';
      if (!domain) return;
      // Seeded once the screen appears, so correcting a near-miss is an edit rather than a retype.
      // Read untracked: tracking the field would make clearing it re-fill itself on the next tick.
      if (!untracked(this.website)) this.website.set(domain);
    });

    // The offer has to follow the field, not the address that failed: taking the suggestion, or
    // typing over it, otherwise leaves "Did you mean acme.com?" standing above acme.com.
    toObservable(this.website)
      .pipe(debounceTime(600), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(website => this.websiteSuggestion.ask(website));
  }

  skip(): void {
    this.orchestrator.skip();
  }

  continue(): void {
    this.orchestrator.dismiss();
  }

  useSuggestedWebsite(): void {
    const suggestion = this.suggestedWebsite();
    if (suggestion) this.website.set(suggestion);
  }

  /** Crawls the address the user just corrected, and keeps it on the organisation. */
  retryWithWebsite(): void {
    const website = this.website().trim();
    if (!website) return;
    this.orchestrator.retryImport(website);
  }

  /** Leaves the failed import behind and opens the upload flow instead. */
  goManual(): void {
    this.orchestrator.dismiss();
    void this.router.navigate([APP_ROUTES.IMPORT_DOCUMENT]);
  }
}
